import { useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { Trans, t } from '@lingui/macro'
import { useLingui } from '@lingui/react'

import { useGetManifestQuery, type Tutorial, type TutorialAssets } from '@/core/api/tutorials'

const FALLBACK_LANG = 'fr'
const SUPPORTED_TUTORIAL_LANGS = ['fr', 'en'] as const
type TutorialLang = (typeof SUPPORTED_TUTORIAL_LANGS)[number]

/**
 * Order in which to look for a tutorial asset, most to least preferred.
 *
 * The manifest currently ships French and English only, so a locale with no
 * dubbed assets (ar-SA in particular) has to borrow another language. It used
 * to borrow French, which is the worst possible choice for a Saudi user —
 * English is far closer to what they can read. French stays first only for
 * French locales, where it is the requested language anyway.
 */
function tutorialLangChain(linguiLocale: string): TutorialLang[] {
  const normalized = linguiLocale.toLowerCase().split('-')[0]
  const requested = (SUPPORTED_TUTORIAL_LANGS as readonly string[]).includes(normalized) ? (normalized as TutorialLang) : undefined
  return [...new Set<TutorialLang>([...(requested ? [requested] : []), 'en', FALLBACK_LANG])]
}

/**
 * Pick the best asset for the requested language: prefer the requested
 * lang, fall back to the FR baseline. Returns the chosen URL and the
 * lang it actually resolved to (so callers can label subtitle tracks).
 */
function langAsset(
  assets: TutorialAssets,
  prefix: 'video' | 'subtitles_vtt' | 'subtitles_srt' | 'narration_txt',
  langs: TutorialLang[],
): { url: string | undefined; lang: TutorialLang } {
  for (const lang of langs) {
    const want = assets[`${prefix}_${lang}`]
    if (want) return { url: want, lang }
  }
  return { url: undefined, lang: langs[langs.length - 1] }
}

function tutorialTitle(t: Tutorial, langs: TutorialLang[]): string {
  for (const lang of langs) {
    const title = t.titles?.[lang]
    if (title) return title
  }
  return t.title
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `0:${String(s).padStart(2, '0')}`
}

interface TutorialPlayerProps {
  tutorial: Tutorial
  langs: TutorialLang[]
}

function TutorialPlayer({ tutorial, langs }: TutorialPlayerProps) {
  const video = langAsset(tutorial.assets, 'video', langs)
  const subs = langAsset(tutorial.assets, 'subtitles_vtt', langs)
  // The <track> label is shown in the browser's caption picker; render it
  // in the actual track language so users see "Français" or "English".
  const trackLabel = subs.lang === 'en' ? t`English` : t`Français`

  return (
    <Paper sx={{ overflow: 'hidden', backgroundColor: '#000' }}>
      {/* Re-mount when tutorial OR lang changes so the browser picks up
          the new <source> + <track> instead of clinging to the old one. */}
      <video
        key={`${tutorial.id}:${video.lang}`}
        controls
        preload="metadata"
        crossOrigin="anonymous"
        style={{ width: '100%', display: 'block', maxHeight: '70vh' }}
      >
        {video.url ? <source src={video.url} type="video/mp4" /> : null}
        {subs.url ? (
          <track
            kind="subtitles"
            label={trackLabel}
            srcLang={subs.lang}
            src={subs.url}
            default
          />
        ) : null}
        {t({ id: 'Votre navigateur ne prend pas en charge la lecture vidéo.', message: 'Your browser does not support video playback.' })}
      </video>
    </Paper>
  )
}

export default function TutorialsPage() {
  const navigate = useNavigate()
  const { id: selectedId } = useParams<{ id: string }>()
  const { i18n } = useLingui()
  const langs = tutorialLangChain(i18n.locale)

  const { data: manifest, isLoading, isError, error } = useGetManifestQuery()

  const tutorials = useMemo(() => {
    if (!manifest?.tutorials) return []
    return [...manifest.tutorials].sort((a, b) => a.order - b.order)
  }, [manifest])

  const selected = useMemo(
    () => tutorials.find((tt) => tt.id === selectedId) ?? tutorials[0],
    [tutorials, selectedId],
  )

  // If we land on /tutorials without an :id, auto-select the first tutorial
  // so users immediately see something.
  useEffect(() => {
    if (!selectedId && tutorials.length > 0) {
      navigate(`/tutorials/${tutorials[0].id}`, { replace: true })
    }
  }, [selectedId, tutorials, navigate])

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
          <CircularProgress />
          <Typography color="text.secondary">
            <Trans id="Chargement des tutoriels…">Loading tutorials…</Trans>
          </Typography>
        </Stack>
      </Container>
    )
  }

  if (isError || !manifest) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" color="error">
            <Trans id="Impossible de charger les tutoriels.">Unable to load tutorials.</Trans>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {String((error as any)?.status ?? '') || <Trans id="Vérifiez votre connexion et réessayez.">Check your connection and try again.</Trans>}
          </Typography>
        </Paper>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1" fontWeight={600}>
          <Trans id="Tutoriels MedConnect">MedConnect tutorials</Trans>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <Trans id="Apprenez à utiliser MedConnect étape par étape.">Learn how to use MedConnect step by step.</Trans>
        </Typography>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        {/* Left: list */}
        <Paper sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0, maxHeight: { md: '70vh' }, overflow: 'auto' }}>
          <List disablePadding>
            {tutorials.map((tutorial) => (
              <ListItemButton
                key={tutorial.id}
                selected={tutorial.id === selected?.id}
                onClick={() => navigate(`/tutorials/${tutorial.id}`)}
              >
                <ListItemText
                  primary={`${tutorial.order}. ${tutorialTitle(tutorial, langs)}`}
                  secondary={formatDuration(tutorial.duration_s)}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: tutorial.id === selected?.id ? 600 : 400 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>

        {/* Right: player + description */}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {selected ? (
            <Stack spacing={2}>
              <Typography variant="h6" component="h2" fontWeight={600}>
                {selected.order}. {tutorialTitle(selected, langs)}
              </Typography>
              <TutorialPlayer tutorial={selected} langs={langs} />
            </Stack>
          ) : (
            <Typography color="text.secondary">
              <Trans id="Sélectionnez un tutoriel pour commencer.">Select a tutorial to get started.</Trans>
            </Typography>
          )}
        </Box>
      </Stack>
    </Container>
  )
}
